import { useMutation } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'

import { userClient } from '../clients/UserClient'
import { useReduxDispatch } from '../store/index'
import { selectUserLoading, logoutAction, selectUserInit } from '../store/user'
import { isEqual } from '../util/isEqual'
import analytics, { AnalyticsEvent, AnalyticsEventTrigger } from '../util/analytics'

import { usePersistedState } from './usePersistedState'

export type LoginFn = (params: {
  email: string
  password: string
}) => Promise<{ userId: string; token: string }>

type ReturnType = {
  token: string | null | undefined
  userId: string | null | undefined
  isLoading: boolean
  isInit: boolean
  logout: () => Promise<void>
  login: LoginFn
}

export const useAuthentication = (params?: {
  onLoginSuccess?: (token: string) => void
  onLoginError?: (error: Error) => void
  onLogout?: () => void
}): ReturnType => {
  // const { value: persistedToken, setValue } = usePersistedState<string | null>('NT_TOKEN', null)
  const { value: credentials, setValue: setCredentials } = usePersistedState<{
    userId: string
    token: string
    companyId: string
  } | null>('NT_CREDS', null)
  const isInit = useSelector(selectUserInit, shallowEqual)

  const dispatch = useReduxDispatch()

  const { mutateAsync: login, isLoading } = useMutation(userClient.login, {
    onSuccess: ({ token, userId, companyId }) => {
      setCredentials({ token, userId, companyId })
      analytics.track(AnalyticsEvent.LoginSuccess, AnalyticsEventTrigger.success)
      params?.onLoginSuccess?.(token)
    },
    onError: (e: Error) => {
      analytics.track(AnalyticsEvent.LoginError, AnalyticsEventTrigger.error)
      params?.onLoginError?.(e)
    },
  })

  const logout = async (): Promise<void> => {
    await dispatch(logoutAction())
    setCredentials(null)
    await userClient.logout()
    params?.onLogout?.()
  }

  const { token, userId } = credentials || {}

  return {
    token,
    userId,
    isLoading,
    isInit,
    logout,
    login,
  }
}
